import type { AuthenticatedPlatformTransport } from './authenticatedPlatformTransport';

/** 积分接口统一使用平台毫积分整数；不使用人民币或技术账本反推用户积分。 */
export type CreditMode = 'off' | 'shadow' | 'live';
/** 查询结果保留失败原因；loading/stale 由页面管理，禁止用空对象或零余额代替失败。 */
export type CreditResult<T> = { status: 'ready'; data: T } | {
  status: 'unauthenticated' | 'forbidden' | 'unsupported' | 'unavailable' | 'superseded'; data: null;
};
/** 平台 /api/credit/self 的用户账户事实；缺少的兼容字段显式保留 null。 */
export interface CreditSelf {
  mode: CreditMode;
  credit_per_quota: number;
  wallet_mcr: number;
  subscription_mcr_total: number;
  subscription_mcr_used: number;
  subscription_mcr_remain: number;
  subscription_unlimited: boolean;
  subscription_count: number | null;
  subscription_base_mcr: number | null;
  subscription_bonus_mcr: number | null;
  next_reset_at: number | null;
  subscription_end_at: number | null;
  today_used_mcr: number | null;
  month_used_mcr: number | null;
  daily_cap_mcr: number | null;
  daily_used_mcr: number | null;
  daily_reset_at: number | null;
  credit_debt_mcr: number | null;
  pool: { remain_mcr: number; total_mcr: number; used_mcr: number; next_reset_at: number | null } | null;
}
/** 发放批次按平台原始类型区分钱包、订阅、奖励；钱包没有可推算的历史总发放量。 */
export interface CreditInventoryItem {
  id: number; kind: string; title: string; source: string;
  total_mcr: number; used_mcr: number; remain_mcr: number;
  start_at: number | null; end_at: number | null; expired: boolean;
}
/** 区间总量直接采用平台聚合；不对截断的明细自行求和。 */
export interface CreditHeatmap {
  mode: CreditMode; days: number; total_mcr: number; total_requests: number;
  rows: Array<{ date: string; quota: number; requests: number }>;
}
/** 会话请求明细与全量总计分开，后续 UI 可据 total_requests 显示明细不完整状态。 */
export interface CreditRequests {
  mode: CreditMode; session_id: string; total_mcr: number; total_requests: number;
  turns: Array<{ turn_id: string; credit_mcr: number; credit_mcr_shadow: number; requests: number }> | null;
  rows: Array<{ request_id: string; client_session_id: string; model_name: string;
    credit_mcr: number; credit_mcr_shadow: number; created_at: number }>;
}
/** 工具与沙箱公开价目，不包含平台采购成本。 */
export interface CreditMeters {
  mode: CreditMode; credit_per_quota: number;
  meters: Array<{ meter_key: string; unit: string; quota_per_unit: number; description: string; requires_confirm: boolean }>;
}
/** 用户自己的账务日志；历史充值/赠送可能仅有说明，不根据 quota 或文案推算金额。 */
export interface CreditHistory {
  page: number; total: number;
  items: Array<{ id: number; type: number; created_at: number; content: string; model_name: string;
    credit_mcr: number | null; credit_mcr_shadow: number | null }>;
}
/** 返回非数组对象，响应格式不符合平台 DTO 时交由查询层统一降级。 */
function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid credit object');
  return value as Record<string, unknown>;
}
/** 检查安全整数，避免把字符串、浮点或缺失金额转成零；钱包可为负余额。 */
function integer(value: unknown, signed = false): number {
  if (!Number.isSafeInteger(value) || (!signed && (value as number) < 0)) throw new Error('Invalid credit integer');
  return value as number;
}
/** 旧平台未返回可选字段时保留未知；已返回但无效的字段不能静默忽略。 */
function optional(value: unknown): number | null { return value == null ? null : integer(value); }
/** 校验计费模式，遇到新模式需适配后才能显示为真实消费。 */
function mode(value: unknown): CreditMode {
  if (value !== 'off' && value !== 'shadow' && value !== 'live') throw new Error('Invalid credit mode');
  return value;
}
/** 校验布尔事实，不能把缺字段自动当成不受限或有效。 */
function boolean(value: unknown): boolean {
  if (typeof value !== 'boolean') throw new Error('Invalid credit boolean');
  return value;
}
/** 白名单读取公开说明文本，不透传接口的额外字段。 */
function text(value: unknown): string { return typeof value === 'string' ? value : ''; }
/** 解析用户积分总览，比例来自平台，缺失比例时拒绝猜测。 */
function self(payload: unknown): CreditSelf {
  const d = object(payload), scale = integer(d.credit_per_quota);
  if (d.unit !== 'credit' || scale <= 0) throw new Error('Invalid credit unit');
  const pool = d.pool == null ? null : object(d.pool);
  return {
    mode: mode(d.mode), credit_per_quota: scale, wallet_mcr: integer(d.wallet_mcr, true),
    subscription_mcr_total: integer(d.subscription_mcr_total), subscription_mcr_used: integer(d.subscription_mcr_used),
    subscription_mcr_remain: integer(d.subscription_mcr_remain), subscription_unlimited: boolean(d.subscription_unlimited),
    subscription_count: optional(d.subscription_count),
    subscription_base_mcr: optional(d.subscription_base_mcr), subscription_bonus_mcr: optional(d.subscription_bonus_mcr),
    next_reset_at: optional(d.next_reset_at), subscription_end_at: optional(d.subscription_end_at),
    today_used_mcr: optional(d.today_used_mcr), month_used_mcr: optional(d.month_used_mcr),
    daily_cap_mcr: optional(d.daily_cap_mcr), daily_used_mcr: optional(d.daily_used_mcr), daily_reset_at: optional(d.daily_reset_at),
    credit_debt_mcr: optional(d.credit_debt_mcr),
    pool: pool ? { remain_mcr: integer(pool.remain_mcr, true), total_mcr: integer(pool.total_mcr),
      used_mcr: integer(pool.used_mcr), next_reset_at: optional(pool.next_reset_at) } : null,
  };
}
/** 积分只在展示时换算；保留毫积分精度，未知值不显示为 0。 */
export function formatCredit(mcr: number | null | undefined, scale: number, locale = 'zh-CN'): string {
  if (mcr == null || !Number.isSafeInteger(mcr) || !Number.isSafeInteger(scale) || scale <= 0) return '—';
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 3 }).format(mcr / scale);
}
/**
 * 创建无共享缓存的用户积分客户端。
 * request 复用现有登录代理；isCurrent 供其在鉴权副作用前丢弃旧账号响应。
 * identity 返回登录周期身份（含账号、登录时间、凭证和平台），未登录返回 null；身份不写入持久存储。
 */
export function createCreditClient(options: AuthenticatedPlatformTransport) {
  /** 执行一次白名单查询，返回明确的不可用/旧平台/过期响应状态，不泄漏服务端错误正文。 */
  async function query<T>(path: string, parse: (data: unknown) => T): Promise<CreditResult<T>> {
    const identity = options.identity();
    if (!identity) return { status: 'unauthenticated', data: null };
    const isCurrent = () => options.identity() === identity;
    try {
      const result = await options.request(path, isCurrent);
      if (!isCurrent()) return { status: 'superseded', data: null };
      if (result.status === 401) return { status: 'unauthenticated', data: null };
      if (result.status === 403) return { status: 'forbidden', data: null };
      if (result.status === 404) return { status: 'unsupported', data: null };
      if (!result.success || result.status < 200 || result.status >= 300) return { status: 'unavailable', data: null };
      return { status: 'ready', data: parse(result.data) };
    } catch {
      return { status: isCurrent() ? 'unavailable' : 'superseded', data: null };
    }
  }
  return {
    /** 按类型分页查询本人记录；0 为全部，1～6 为平台日志类型，每页固定 20 条。 */
    history: (type = 0, page = 1): Promise<CreditResult<CreditHistory>> => {
      if (!Number.isInteger(type) || type < 0 || type > 6 || !Number.isSafeInteger(page) || page < 1) {
        return Promise.resolve({ status: 'unavailable', data: null });
      }
      return query(`/api/log/self?type=${type}&p=${page}&page_size=20`, payload => {
        const d = object(payload);
        if (d.page !== page || d.page_size !== 20 || !Array.isArray(d.items) || d.items.length > 20) throw new Error('Invalid history page');
        return { page, total: integer(d.total), items: d.items.map(value => {
          const r = object(value);
          return { id: integer(r.id), type: integer(r.type), created_at: integer(r.created_at), content: text(r.content),
            model_name: text(r.model_name), credit_mcr: optional(r.credit_mcr), credit_mcr_shadow: optional(r.credit_mcr_shadow) };
        }) };
      });
    },
    /** 获取本用户余额、周期用量和企业池；不读取超管账本。 */
    self: () => query('/api/credit/self', self),
    /** 获取发放来源、剩余与到期批次；空数组仅代表平台确实返回空记录。 */
    inventory: () => query('/api/credit/self/inventory', payload => {
      const d = object(payload);
      if (!Array.isArray(d.items)) throw new Error('Invalid inventory');
      return d.items.map((value): CreditInventoryItem => {
        const row = object(value);
        return { id: integer(row.id), kind: text(row.kind), title: text(row.title), source: text(row.source),
          total_mcr: integer(row.total_mcr), used_mcr: integer(row.used_mcr), remain_mcr: integer(row.remain_mcr),
          start_at: optional(row.start_at), end_at: optional(row.end_at), expired: boolean(row.expired) };
      });
    }),
    /** 获取 1～731 天平台权威区间用量；天数不合法时不发送请求。 */
    heatmap: (days = 30): Promise<CreditResult<CreditHeatmap>> => {
      if (!Number.isInteger(days) || days < 1 || days > 731) return Promise.resolve({ status: 'unavailable', data: null });
      return query(`/api/credit/self/heatmap?days=${days}`, payload => {
        const d = object(payload);
        if (d.days !== days || !Array.isArray(d.rows)) throw new Error('Invalid heatmap');
        return { mode: mode(d.mode), days, total_mcr: integer(d.total_mcr), total_requests: integer(d.total_requests),
          rows: d.rows.map(value => { const r = object(value); return { date: text(r.date), quota: integer(r.quota), requests: integer(r.requests) }; }) };
      });
    },
    /** 工具/运行时计价只读取公开价目，保留平台给出的单位及确认要求。 */
    meters: (): Promise<CreditResult<CreditMeters>> => query('/api/credit/meters', payload => {
      const d = object(payload), scale = integer(d.credit_per_quota);
      if (!Array.isArray(d.meters) || scale <= 0) throw new Error('Invalid credit meters');
      return { mode: mode(d.mode), credit_per_quota: scale, meters: d.meters.map(value => {
        const r = object(value);
        return { meter_key: text(r.meter_key), unit: text(r.unit), quota_per_unit: integer(r.quota_per_unit), description: text(r.description), requires_confirm: boolean(r.requires_confirm) };
      }) };
    }),
    /** 获取指定会话的全历史总量与最近明细，强制回显匹配，防止旧平台返回账户全量。 */
    requests: (sessionId: string, limit = 100): Promise<CreditResult<CreditRequests>> => {
      if (!sessionId || sessionId.length > 128 || !Number.isInteger(limit) || limit < 1 || limit > 200) {
        return Promise.resolve({ status: 'unavailable', data: null });
      }
      const params = new URLSearchParams({ session_id: sessionId, limit: String(limit), include_turns: '1' });
      return query(`/api/credit/self/requests?${params}`, payload => {
        const d = object(payload);
        if (d.session_id !== sessionId || !Array.isArray(d.rows)) throw new Error('Invalid credit session');
        return { mode: mode(d.mode), session_id: sessionId, total_mcr: integer(d.total_mcr), total_requests: integer(d.total_requests),
          turns: d.turns == null ? null : (() => {
            if (!Array.isArray(d.turns)) throw new Error('Invalid credit turns');
            const ids = new Set<string>();
            return d.turns.map(value => {
              const row = object(value);
              if (typeof row.turn_id !== 'string' || ids.has(row.turn_id)) throw new Error('Invalid credit turn ID');
              ids.add(row.turn_id);
              return { turn_id: row.turn_id, credit_mcr: integer(row.credit_mcr), credit_mcr_shadow: integer(row.credit_mcr_shadow), requests: integer(row.requests) };
            });
          })(),
          rows: d.rows.map(value => {
            const r = object(value);
            if (r.client_session_id !== sessionId) throw new Error('Invalid credit row session');
            return { request_id: text(r.request_id), client_session_id: sessionId, model_name: text(r.model_name),
              credit_mcr: integer(r.credit_mcr), credit_mcr_shadow: integer(r.credit_mcr_shadow), created_at: integer(r.created_at) };
          }) };
      });
    },
  };
}
